import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdCloudUpload,
    MdChecklist,
    MdContentCopy,
    MdCreateNewFolder,
    MdDelete,
    MdDownload,
    MdDriveFileMove,
    MdEdit,
    MdFolder,
    MdGridView,
    MdInfoOutline,
    MdLink,
    MdLock,
    MdLockOpen,
    MdMoreVert,
    MdRestore,
    MdSearch,
    MdShield,
    MdViewList,
    MdVisibility,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import FilePreviewModal, {
    canPreview,
    type PreviewFile,
} from '@/components/file-preview'
import { usePermission } from '@/lib/hooks/use-permission'
import {
    confirmVersion,
    copyFile,
    createFolder,
    createVersionIntent,
    deleteFile,
    deleteFolder,
    getDownloadUrl,
    getFolderBreadcrumb,
    getFolderLocks,
    getUsage,
    listFiles,
    listFolders,
    moveFile,
    moveFolder,
    purgeFile,
    restoreFile,
    restoreFolder,
    setFilePassword,
    setFolderPassword,
    unlockFile,
    unlockFolder,
    updateFile,
    updateFolder,
    uploadToSignedUrl,
    type StorageFile,
    type StorageFolder,
    type StorageUsage,
} from '@/lib/api/storage/storage-api'
import StorageUploadModal from './storage-upload-modal'
import { DROP_FOLDER_ATTRIBUTE, usePageFileDrop } from './use-page-file-drop'
import StoragePasswordModal from './storage-password-modal'
import StorageShareModal from './storage-share-modal'
import StorageAccessModal from './storage-access-modal'
import StorageMoveModal from './storage-move-modal'
import StorageRolePicker from './storage-role-picker'
import {
    colorOf,
    folderColorClass,
    formatBytes,
    formatDateTime,
    iconOf,
} from './storage-utils'

/** Chave da preferência de visualização (grade ou lista). */
const VIEW_MODE_KEY = 'storage:view-mode'

/**
 * Fecha o menu de ações. O dropdown do DaisyUI é controlado por foco: sem tirar
 * o foco ele segue aberto depois do clique no item. Com `<details>` era pior —
 * não fechava nem ao clicar fora.
 */
const closeDropdown = () =>
    (document.activeElement as HTMLElement | null)?.blur()

type ViewMode = 'grid' | 'list'
type PasswordTarget =
    | { kind: 'file'; id: number; name: string; hint?: string | null; hasPassword: boolean }
    | { kind: 'folder'; id: number; name: string; hint?: string | null; hasPassword: boolean }

export default function StorageFilesPage() {
    const [searchParams, setSearchParams] = useSearchParams()
    const { canCreate, canUpdate, canDelete, hasPermission } = usePermission()

    const folderId = searchParams.get('folder') ? Number(searchParams.get('folder')) : null
    const trash = searchParams.get('trash') === 'true'

    const [folders, setFolders] = useState<StorageFolder[]>([])
    const [files, setFiles] = useState<StorageFile[]>([])
    const [breadcrumb, setBreadcrumb] = useState<StorageFolder[]>([])
    const [usage, setUsage] = useState<StorageUsage | null>(null)
    const [loading, setLoading] = useState(true)

    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    // A escolha entre grade e lista é preferência de trabalho, não estado de
    // navegação: quem prefere lista não quer reescolher a cada visita.
    const [viewMode, setViewMode] = useState<ViewMode>(() => {
        const saved = localStorage.getItem(VIEW_MODE_KEY)
        return saved === 'list' || saved === 'grid' ? saved : 'grid'
    })

    useEffect(() => {
        localStorage.setItem(VIEW_MODE_KEY, viewMode)
    }, [viewMode])

    const [uploadOpen, setUploadOpen] = useState(false)
    const [droppedFiles, setDroppedFiles] = useState<File[] | null>(null)
    /** Destino da leva arrastada: a pasta mirada, ou a aberta. */
    const [uploadTargetFolderId, setUploadTargetFolderId] = useState<number | null>(null)
    /** Cada envio é uma sessão; trocar a chave remonta o modal com a leva nova. */
    const [uploadSession, setUploadSession] = useState(0)
    const [newFolderOpen, setNewFolderOpen] = useState(false)
    const [newFolderName, setNewFolderName] = useState('')
    const [newFolderPassword, setNewFolderPassword] = useState('')
    /**
     * O campo de senha só é MONTADO quando o operador pede.
     *
     * Um `input type="password"` presente no formulário faz o Chrome tratar a tela
     * como login e preencher a senha salva sozinho — e toda pasta criada nascia
     * protegida por uma senha que ninguém digitou. `autoComplete="off"` não resolve;
     * o que resolve é o campo não existir no DOM até ser pedido. Mesma raiz do
     * problema que o step-up de senha causava nas telas de busca.
     */
    const [newFolderPasswordEnabled, setNewFolderPasswordEnabled] = useState(false)
    const [newFolderRestricted, setNewFolderRestricted] = useState(false)
    const [newFolderRoleIds, setNewFolderRoleIds] = useState<number[]>([])
    const [savingFolder, setSavingFolder] = useState(false)

    const [accessFolder, setAccessFolder] = useState<StorageFolder | null>(null)

    const [renaming, setRenaming] = useState<{ kind: 'file' | 'folder'; id: number } | null>(null)
    const [renameValue, setRenameValue] = useState('')

    const [passwordModal, setPasswordModal] = useState<{
        mode: 'unlock' | 'set'
        target: PasswordTarget
    } | null>(null)
    const [passwordLoading, setPasswordLoading] = useState(false)
    /**
     * O que abrir depois de um desbloqueio bem-sucedido. Alterar ou remover a
     * senha exige a pasta/arquivo destravado no servidor; sem guardar a
     * intenção, o desbloqueio devolveria o usuário à listagem e ele teria de
     * procurar o menu de novo.
     */
    const [passwordSettingsAfterUnlock, setPasswordSettingsAfterUnlock] =
        useState<PasswordTarget | null>(null)

    const [shareFile, setShareFile] = useState<StorageFile | null>(null)
    const [detailsFile, setDetailsFile] = useState<StorageFile | null>(null)
    const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null)
    /** Arquivo cuja URL está sendo assinada. Trava o clique repetido. */
    const [openingFileId, setOpeningFileId] = useState<number | null>(null)

    const [confirmDelete, setConfirmDelete] = useState<{
        kind: 'file' | 'folder'
        id: number
        name: string
        permanent?: boolean
    } | null>(null)
    const [deleting, setDeleting] = useState(false)

    /**
     * Seleção para ações em lote. Guarda ids, e não os objetos: a lista é
     * recarregada a cada operação, e um objeto guardado ficaria defasado.
     */
    /**
     * Modo de seleção. Os checkboxes só existem quando ele está ligado: numa
     * pasta cheia, uma caixa por card poluiria a tela para todo mundo, e a
     * seleção em lote é ação ocasional.
     */
    const [selectionMode, setSelectionMode] = useState(false)
    const [selectedFileIds, setSelectedFileIds] = useState<number[]>([])
    const [selectedFolderIds, setSelectedFolderIds] = useState<number[]>([])
    /** Itens a mover: a seleção, ou um único item vindo do menu de ações. */
    const [movePayload, setMovePayload] = useState<{
        fileIds: number[]
        folderIds: number[]
    } | null>(null)
    const [moving, setMoving] = useState(false)

    // Debounce da busca: cada tecla dispara uma consulta ao servidor.
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 400)
        return () => clearTimeout(timer)
    }, [search])

    const load = useCallback(async () => {
        setLoading(true)

        const [foldersResponse, filesResponse] = await Promise.all([
            debouncedSearch ? Promise.resolve(null) : listFolders(folderId, trash),
            listFiles({
                folderId: folderId ?? undefined,
                search: debouncedSearch || undefined,
                trash: trash || undefined,
                limit: 100,
            }),
        ])

        if (foldersResponse) {
            setFolders(foldersResponse.success ? (foldersResponse.result ?? []) : [])
        } else {
            setFolders([])
        }

        if (filesResponse.success) {
            setFiles(filesResponse.result?.items ?? [])
        } else {
            // Pasta protegida devolve 403 até o desbloqueio: a mensagem do
            // backend já explica o que fazer, então não vira erro genérico.
            setFiles([])
            if (filesResponse.error) toast.info(filesResponse.error)
        }

        setLoading(false)
    }, [folderId, trash, debouncedSearch])

    const loadBreadcrumb = useCallback(async () => {
        if (!folderId) {
            setBreadcrumb([])
            return
        }
        const response = await getFolderBreadcrumb(folderId)
        setBreadcrumb(response.success ? (response.result ?? []) : [])
    }, [folderId])

    const loadUsage = useCallback(async () => {
        const response = await getUsage()
        if (response.success) setUsage(response.result ?? null)
    }, [])

    useEffect(() => {
        void load()
    }, [load])

    useEffect(() => {
        void loadBreadcrumb()
    }, [loadBreadcrumb])

    useEffect(() => {
        void loadUsage()
    }, [loadUsage])

    /**
     * Ao entrar numa pasta, checa se há senha pendente no caminho e já abre o
     * modal — sem isso o usuário veria uma pasta vazia sem entender por quê.
     */
    const openFolder = async (folder: StorageFolder) => {
        if (folder.hasPassword) {
            const locks = await getFolderLocks(folder.id)
            const pending = locks.success ? (locks.result ?? []) : []
            if (pending.length) {
                setPasswordModal({
                    mode: 'unlock',
                    target: {
                        kind: 'folder',
                        id: pending[0].id,
                        name: pending[0].name,
                        hint: pending[0].passwordHint,
                        hasPassword: true,
                    },
                })
                return
            }
        }
        setSearchParams({ folder: String(folder.id) })
    }

    /**
     * Abre a tela de senha do item — pedindo o desbloqueio antes, quando ele já
     * está protegido.
     *
     * O backend recusa alterar ou remover a senha de um item travado (a pasta
     * protegida do caminho, inclusive), e o modal de definição não tem como
     * destravar nada. Sem este desvio, "Remover proteção" respondia
     * "Desbloqueie para continuar" e a tela não oferecia caminho nenhum.
     */
    const openPasswordSettings = async (target: PasswordTarget) => {
        if (!target.hasPassword) {
            setPasswordModal({ mode: 'set', target })
            return
        }

        if (target.kind === 'folder') {
            // A trava pode estar numa pasta ANCESTRAL, e é ela que precisa da
            // senha primeiro. Já desbloqueada, vai direto para a definição.
            const locks = await getFolderLocks(target.id)
            const pending = locks.success ? (locks.result ?? []) : []
            if (!pending.length) {
                setPasswordModal({ mode: 'set', target })
                return
            }

            setPasswordSettingsAfterUnlock(target)
            setPasswordModal({
                mode: 'unlock',
                target: {
                    kind: 'folder',
                    id: pending[0].id,
                    name: pending[0].name,
                    hint: pending[0].passwordHint,
                    hasPassword: true,
                },
            })
            return
        }

        // Arquivo não tem consulta de trava: a senha atual é pedida sempre
        // antes de trocá-la ou removê-la.
        setPasswordSettingsAfterUnlock(target)
        setPasswordModal({ mode: 'unlock', target })
    }

    const navigateTo = (id: number | null) => {
        if (id) setSearchParams({ folder: String(id) })
        else setSearchParams({})
    }

    const closeNewFolder = () => {
        setNewFolderOpen(false)
        setNewFolderName('')
        setNewFolderPassword('')
        setNewFolderPasswordEnabled(false)
        setNewFolderRestricted(false)
        setNewFolderRoleIds([])
    }

    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) {
            toast.error('Informe o nome da pasta')
            return
        }
        if (newFolderRestricted && !newFolderRoleIds.length) {
            toast.error('Selecione ao menos um perfil de acesso para a pasta restrita')
            return
        }

        setSavingFolder(true)
        const response = await createFolder({
            name: newFolderName.trim(),
            parentId: folderId,
            // Sem o pedido explícito não vai senha nenhuma — nem o que um
            // preenchimento automático tenha deixado no estado.
            password: newFolderPasswordEnabled
                ? newFolderPassword || undefined
                : undefined,
            roleIds: newFolderRestricted ? newFolderRoleIds : undefined,
        })
        setSavingFolder(false)

        if (!response.success) {
            toast.error(response.error || 'Não foi possível criar a pasta')
            return
        }

        toast.success(
            newFolderRestricted ? 'Pasta restrita criada com sucesso' : 'Pasta criada com sucesso',
        )
        closeNewFolder()
        void load()
    }

    /**
     * Salva o conteúdo editado como NOVA VERSÃO, e não por cima do objeto atual.
     *
     * Editar texto é a única forma de alterar um arquivo já enviado, e passar
     * por cima significaria perder o original sem nenhum caminho de volta. Como
     * versão, o histórico do arquivo permite restaurar.
     */
    const saveTextVersion = async (file: StorageFile, content: string) => {
        const mimeType = file.mimeType || 'text/plain'
        const blob = new Blob([content], { type: mimeType })

        const intent = await createVersionIntent(file.id, {
            name: file.name,
            mimeType,
            sizeBytes: blob.size,
        })

        if (!intent.success || !intent.result?.uploadUrl) {
            const message =
                intent.error || 'Não foi possível iniciar o envio da nova versão'
            toast.error(message)
            throw new Error(message)
        }

        try {
            await uploadToSignedUrl(intent.result.uploadUrl, blob, mimeType)
        } catch (error) {
            toast.error((error as Error).message)
            throw error
        }

        const confirmed = await confirmVersion(file.id)
        if (!confirmed.success) {
            const message = confirmed.error || 'Não foi possível salvar a nova versão'
            toast.error(message)
            throw new Error(message)
        }

        toast.success('Nova versão salva com sucesso')
        void load()
    }

    const handleDownload = async (file: StorageFile, inline = false) => {
        // Clique repetido enquanto a URL não chega dispararia uma assinatura
        // por clique — e é justamente o que o usuário faz quando a tela não
        // responde. A trava vem antes de qualquer outra coisa.
        if (openingFileId === file.id) return
        setOpeningFileId(file.id)

        // O modal abre AGORA, com o nome do arquivo e um indicador. Assinar a
        // URL é uma ida ao servidor; sem isto, o intervalo é uma tela parada.
        const willPreview = inline && canPreview(file.name, file.mimeType)
        if (willPreview) {
            setPreviewFile({ fileName: file.name, mimeType: file.mimeType })
        }

        const response = await getDownloadUrl(file.id, inline).finally(() =>
            setOpeningFileId(null),
        )

        if (!response.success || !response.result) {
            if (willPreview) setPreviewFile(null)

            // Arquivo protegido: abre o modal de senha em vez de só reclamar.
            if (file.hasPassword) {
                setPasswordModal({
                    mode: 'unlock',
                    target: {
                        kind: 'file',
                        id: file.id,
                        name: file.name,
                        hint: file.passwordHint,
                        hasPassword: true,
                    },
                })
                return
            }
            toast.error(response.error || 'Não foi possível abrir o arquivo')
            return
        }

        const { url, name, mimeType } = response.result
        const fileName = name || file.name

        // "Abrir" mostra aqui mesmo; "Baixar" continua indo para o navegador.
        //
        // O tipo é reavaliado com o que o servidor respondeu: o `mimeType` de
        // lá é a fonte boa, e o palpite feito no clique usou o da listagem.
        if (inline && canPreview(fileName, mimeType)) {
            setPreviewFile({
                url,
                fileName,
                mimeType,
                onSaveText: canUpdateFiles
                    ? content => saveTextVersion(file, content)
                    : undefined,
            })
            return
        }

        // O palpite do clique abriu o modal, mas o servidor diz que não há como
        // exibir: fecha e cai no comportamento de download.
        if (willPreview) setPreviewFile(null)

        window.open(url, '_blank', 'noopener')
    }

    const handleRename = async () => {
        if (!renaming || !renameValue.trim()) {
            setRenaming(null)
            return
        }

        const response =
            renaming.kind === 'file'
                ? await updateFile(renaming.id, { name: renameValue.trim() })
                : await updateFolder(renaming.id, { name: renameValue.trim() })

        if (!response.success) {
            toast.error(response.error || 'Não foi possível renomear')
            return
        }

        toast.success('Renomeado com sucesso')
        setRenaming(null)
        void load()
    }

    const handlePasswordConfirm = async (password: string | null, hint?: string) => {
        if (!passwordModal) return
        const { mode, target } = passwordModal
        setPasswordLoading(true)

        let response
        if (mode === 'unlock') {
            response =
                target.kind === 'file'
                    ? await unlockFile(target.id, password ?? '')
                    : await unlockFolder(target.id, password ?? '')
        } else {
            response =
                target.kind === 'file'
                    ? await setFilePassword(target.id, password, hint)
                    : await setFolderPassword(target.id, password, hint)
        }

        setPasswordLoading(false)

        if (!response.success) {
            toast.error(response.error || 'Operação não concluída')
            return
        }

        toast.success(
            mode === 'unlock'
                ? 'Desbloqueado com sucesso'
                : password
                  ? 'Senha definida com sucesso'
                  : 'Proteção removida',
        )

        // Desbloqueio feito para chegar à definição de senha: volta para ela em
        // vez de navegar para a pasta. A cadeia pode ter mais de uma trava, e
        // `openPasswordSettings` pede a próxima sozinho.
        if (mode === 'unlock' && passwordSettingsAfterUnlock) {
            const next = passwordSettingsAfterUnlock
            setPasswordSettingsAfterUnlock(null)
            setPasswordModal(null)
            void openPasswordSettings(next)
            return
        }

        if (mode === 'unlock' && target.kind === 'folder') {
            setSearchParams({ folder: String(target.id) })
        }

        setPasswordModal(null)
        void load()
    }

    const handleDelete = async () => {
        if (!confirmDelete) return
        setDeleting(true)

        const response = confirmDelete.permanent
            ? await purgeFile(confirmDelete.id)
            : confirmDelete.kind === 'file'
              ? await deleteFile(confirmDelete.id)
              : await deleteFolder(confirmDelete.id)

        setDeleting(false)

        if (!response.success) {
            toast.error(response.error || 'Não foi possível excluir')
            return
        }

        toast.success(
            confirmDelete.permanent ? 'Excluído definitivamente' : 'Movido para a lixeira',
        )
        setConfirmDelete(null)
        void load()
        void loadUsage()
    }

    const handleRestore = async (kind: 'file' | 'folder', id: number) => {
        const response = kind === 'file' ? await restoreFile(id) : await restoreFolder(id)

        if (!response.success) {
            toast.error(response.error || 'Não foi possível restaurar')
            return
        }

        toast.success('Restaurado com sucesso')
        void load()
    }

    /**
     * Trocar de pasta, entrar na lixeira ou buscar troca a lista inteira: uma
     * seleção que sobrevivesse moveria itens que o usuário não vê mais.
     */
    useEffect(() => {
        setSelectedFileIds([])
        setSelectedFolderIds([])
    }, [folderId, trash, debouncedSearch])

    /** Na lixeira não se move nada: o modo não sobrevive à entrada nela. */
    useEffect(() => {
        if (trash) setSelectionMode(false)
    }, [trash])

    const exitSelectionMode = () => {
        setSelectionMode(false)
        setSelectedFileIds([])
        setSelectedFolderIds([])
    }

    const toggleFile = (id: number) =>
        setSelectedFileIds(current =>
            current.includes(id)
                ? current.filter(item => item !== id)
                : [...current, id],
        )

    const toggleFolder = (id: number) =>
        setSelectedFolderIds(current =>
            current.includes(id)
                ? current.filter(item => item !== id)
                : [...current, id],
        )

    const clearSelection = () => {
        setSelectedFileIds([])
        setSelectedFolderIds([])
    }

    const selectionCount = selectedFileIds.length + selectedFolderIds.length
    const allSelected =
        selectionCount > 0 &&
        selectedFileIds.length === files.length &&
        selectedFolderIds.length === folders.length

    const toggleAll = () => {
        if (allSelected) {
            clearSelection()
            return
        }
        setSelectedFileIds(files.map(file => file.id))
        setSelectedFolderIds(folders.map(folder => folder.id))
    }

    /**
     * Move a seleção para a pasta escolhida.
     *
     * As chamadas vão em sequência, e não em paralelo: cada movimentação
     * recalcula o tamanho das pastas de origem e destino no servidor, e
     * disparar tudo de uma vez faria esses recálculos competirem entre si. O
     * que falhar é contado — um arquivo protegido por senha, por exemplo, é
     * recusado individualmente e não pode derrubar o lote inteiro.
     */
    const handleMove = async (targetFolderId: number | null) => {
        if (!movePayload) return

        setMoving(true)
        let moved = 0
        const failures: string[] = []

        for (const id of movePayload.folderIds) {
            const response = await moveFolder(id, targetFolderId)
            if (response.success) moved += 1
            else failures.push(response.error || 'Falha ao mover a pasta')
        }

        for (const id of movePayload.fileIds) {
            const response = await moveFile(id, targetFolderId)
            if (response.success) moved += 1
            else failures.push(response.error || 'Falha ao mover o arquivo')
        }

        setMoving(false)
        setMovePayload(null)
        exitSelectionMode()

        if (moved) {
            toast.success(
                moved === 1
                    ? 'Item movido com sucesso'
                    : `${moved} itens movidos com sucesso`,
            )
        }
        // Mostra o motivo do primeiro recusado: repetir a mesma mensagem uma vez
        // por item viraria uma pilha de avisos idênticos.
        if (failures.length) {
            toast.error(
                failures.length === 1
                    ? failures[0]
                    : `${failures.length} itens não foram movidos: ${failures[0]}`,
            )
        }

        void load()
    }

    const handleCopy = async (file: StorageFile) => {
        const response = await copyFile(file.id, file.folderId)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível duplicar')
            return
        }
        toast.success('Arquivo duplicado')
        void load()
        void loadUsage()
    }

    const usagePercent = usage?.percent ?? 0
    const usageColor =
        usagePercent >= 90
            ? 'progress-error'
            : usagePercent >= 75
              ? 'progress-warning'
              : 'progress-primary'

    const isEmpty = !loading && !folders.length && !files.length

    const canCreateFiles = canCreate('storage', 'files')
    const canUpdateFiles = canUpdate('storage', 'files')
    const canDeleteFiles = canDelete('storage', 'files')
    const canShare = hasPermission('storage', 'shares', 'create')

    const currentFolder = useMemo(
        () => breadcrumb[breadcrumb.length - 1] ?? null,
        [breadcrumb],
    )

    /**
     * Solto sobre um card de pasta, o arquivo vai para ELA; solto em qualquer
     * outro ponto, vai para a pasta aberta no momento.
     *
     * O modal é remontado com uma `key` nova a cada leva, e não apenas
     * reaberto: sem isso, soltar um segundo grupo de arquivos com o modal ainda
     * montado não teria efeito nenhum.
     */
    const handleDroppedFiles = useCallback(
        (files: File[], skippedFolders: number, targetFolderId: number | null) => {
            if (skippedFolders) {
                toast.info(
                    skippedFolders === 1
                        ? 'Pastas ainda não podem ser enviadas por arraste. Envie os arquivos de dentro dela.'
                        : `${skippedFolders} pastas foram ignoradas: o envio por arraste aceita apenas arquivos.`,
                )
            }
            if (!files.length) return

            setDroppedFiles(files)
            setUploadTargetFolderId(targetFolderId ?? folderId)
            setUploadSession(session => session + 1)
            setUploadOpen(true)
        },
        [folderId],
    )

    // Na lixeira não se cria arquivo, e sem permissão o arraste não deve nem
    // sinalizar que aceita. Com o visualizador aberto, o arquivo solto seria
    // para lá, não para a pasta atrás dele.
    const { draggingFiles, dropFolderId } = usePageFileDrop({
        accept: canCreateFiles && !trash && !uploadOpen && !previewFile,
        onFiles: handleDroppedFiles,
    })

    /**
     * Marca o elemento como pasta de destino do arraste.
     *
     * O atributo fica SEMPRE presente, e não só durante o arraste: condicioná-lo
     * a um estado criaria uma corrida — o `dragenter` agenda o render, e o
     * `dragover` seguinte procuraria um atributo que o React ainda não pintou.
     * Na lixeira ele é inofensivo, porque ali a página não aceita soltura.
     */
    const folderDropProps = (id: number) => ({ [DROP_FOLDER_ATTRIBUTE]: id })

    /** Nome exibido na sobreposição: a pasta mirada, ou a pasta aberta. */
    const dropTargetName = useMemo(() => {
        if (dropFolderId != null) {
            const target = folders.find(folder => folder.id === dropFolderId)
            if (target) return target.name
        }
        return currentFolder?.name ?? null
    }, [dropFolderId, folders, currentFolder])

    return (
        <div className="flex flex-col gap-5">
            {/*
             * Aviso do arraste.
             *
             * `pointer-events-none` é essencial: uma camada que captura o mouse
             * rouba o `drop` da janela, e o arquivo solto não chegaria a lugar
             * nenhum.
             *
             * E é um aviso rodapé, não uma cortina sobre a tela: como dá para
             * mirar uma pasta, escurecer ou desfocar tudo esconderia justamente
             * o alvo. Quem destaca o destino é o card, não esta camada.
             */}
            {draggingFiles && (
                <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[70] flex justify-center px-4">
                    <div className="flex items-center gap-3 rounded-full border-2 border-dashed border-primary bg-base-100/95 px-6 py-3 shadow-2xl">
                        <MdCloudUpload className="text-3xl text-primary" />
                        <div className="min-w-0">
                            <p className="font-semibold leading-tight">
                                Solte para enviar
                            </p>
                            <p className="truncate text-sm text-base-content/60">
                                {dropFolderId != null && dropTargetName
                                    ? `Para a pasta "${dropTargetName}"`
                                    : dropTargetName
                                      ? `Para "${dropTargetName}" — ou solte sobre uma pasta`
                                      : 'Para a pasta raiz — ou solte sobre uma pasta'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <header className="relative overflow-hidden rounded-2xl border border-base-300 bg-base-100">
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 bg-linear-to-r from-secondary/10 via-primary/5 to-transparent"
                />
                <div className="relative flex flex-wrap items-center gap-4 p-5">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary ring-1 ring-secondary/20">
                        {trash ? (
                            <MdDelete className="text-2xl" />
                        ) : (
                            <MdFolder className="text-2xl" />
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <h1 className="truncate text-xl font-bold text-base-content sm:text-2xl">
                            {trash ? 'Lixeira' : 'Armazenamento'}
                        </h1>
                        <p className="text-sm text-base-content/50">
                            {trash
                                ? 'Itens excluídos, ainda recuperáveis'
                                : 'Arquivos e pastas da sua organização'}
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            className={`btn btn-sm gap-2 ${
                                trash ? 'btn-secondary' : 'btn-ghost'
                            }`}
                            onClick={() =>
                                trash
                                    ? setSearchParams({})
                                    : setSearchParams({ trash: 'true' })
                            }
                        >
                            <MdDelete className="text-base" />
                            {trash ? 'Sair da lixeira' : 'Lixeira'}
                        </button>

                        {!trash && canCreateFiles && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline btn-secondary gap-2"
                                    onClick={() => setNewFolderOpen(true)}
                                >
                                    <MdCreateNewFolder className="text-base" />
                                    <span className="hidden sm:inline">
                                        Nova pasta
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary gap-2"
                                    onClick={() => setUploadOpen(true)}
                                >
                                    <MdCloudUpload className="text-base" />
                                    Enviar
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </header>

            {/* Quota como nota de rodapé do cabeçalho, não como cartão: é
                informação de fundo, consultada de vez em quando, e não compete
                com os arquivos. Ganha destaque só quando passa a importar —
                perto do limite o texto muda de cor e o aviso aparece. */}
            {usage && (
                <div className="-mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-xs">
                    <span className="h-1 w-24 shrink-0 overflow-hidden rounded-full bg-base-300">
                        <span
                            className={`block h-full rounded-full ${
                                usagePercent >= 90
                                    ? 'bg-error'
                                    : usagePercent >= 75
                                      ? 'bg-warning'
                                      : 'bg-base-content/30'
                            }`}
                            style={{ width: `${Math.min(100, usagePercent)}%` }}
                        />
                    </span>
                    <span
                        className={
                            usagePercent >= 90
                                ? 'font-medium text-error'
                                : 'text-base-content/45'
                        }
                    >
                        {formatBytes(usage.usedBytes)}
                        {usage.unlimited
                            ? ' usados'
                            : ` de ${formatBytes(usage.limitBytes ?? '0')}`}
                    </span>
                    <span className="text-base-content/30">·</span>
                    <span className="text-base-content/45">
                        {usage.fileCount}{' '}
                        {usage.fileCount === 1 ? 'arquivo' : 'arquivos'}
                    </span>
                    {usagePercent >= 90 && (
                        <span className="text-error">
                            {/* Quando o teto é o do plano, "aumentar a cota" não
                                é uma opção — o caminho é contratar espaço. */}
                            {usage.selfLimitBytes
                                ? '— libere espaço na lixeira ou aumente o limite em Configurações'
                                : '— libere espaço na lixeira ou contrate GB adicionais em Configurações'}
                        </span>
                    )}
                </div>
            )}

            <div className="flex flex-col gap-3 rounded-2xl border border-base-300 bg-base-100 p-4 sm:flex-row sm:items-center">
                <div className="relative w-full sm:max-w-xs">
                    {/* z-10: sem isso o ícone fica atrás do fundo do input. */}
                    <MdSearch className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-lg text-base-content/40" />
                    <input
                        type="search"
                        className="input input-bordered input-sm w-full pl-9"
                        placeholder="Buscar em todos os arquivos"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                    />
                </div>

                {!trash && !debouncedSearch && (
                    <nav
                        aria-label="Caminho"
                        className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto text-sm"
                    >
                        <button
                            type="button"
                            className={`shrink-0 rounded-lg px-2 py-1 transition-colors ${
                                breadcrumb.length === 0
                                    ? 'font-semibold text-base-content'
                                    : 'text-base-content/60 hover:bg-base-200'
                            }`}
                            onClick={() => navigateTo(null)}
                        >
                            Início
                        </button>
                        {breadcrumb.map((item, index) => {
                            const last = index === breadcrumb.length - 1
                            return (
                                <span
                                    key={item.id}
                                    className="flex shrink-0 items-center gap-1"
                                >
                                    <span className="text-base-content/25">
                                        /
                                    </span>
                                    <button
                                        type="button"
                                        className={`max-w-[12rem] truncate rounded-lg px-2 py-1 transition-colors ${
                                            last
                                                ? 'font-semibold text-base-content'
                                                : 'text-base-content/60 hover:bg-base-200'
                                        }`}
                                        onClick={() => navigateTo(item.id)}
                                    >
                                        {item.name}
                                    </button>
                                </span>
                            )
                        })}
                    </nav>
                )}

                {canUpdateFiles && !trash && (
                    <button
                        type="button"
                        className={`btn btn-sm shrink-0 gap-2 sm:ml-auto ${
                            selectionMode ? 'btn-secondary' : 'btn-ghost'
                        }`}
                        onClick={() =>
                            selectionMode
                                ? exitSelectionMode()
                                : setSelectionMode(true)
                        }
                        aria-pressed={selectionMode}
                    >
                        <MdChecklist className="text-base" />
                        {selectionMode ? 'Cancelar seleção' : 'Selecionar'}
                    </button>
                )}

                <div className={`join shrink-0 ${canUpdateFiles && !trash ? '' : 'sm:ml-auto'}`}>
                    <button
                        type="button"
                        className={`btn btn-sm join-item ${
                            viewMode === 'grid' ? 'btn-secondary' : 'btn-ghost'
                        }`}
                        onClick={() => setViewMode('grid')}
                        title="Grade"
                        aria-pressed={viewMode === 'grid'}
                    >
                        <MdGridView />
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm join-item ${
                            viewMode === 'list' ? 'btn-secondary' : 'btn-ghost'
                        }`}
                        onClick={() => setViewMode('list')}
                        title="Lista"
                        aria-pressed={viewMode === 'list'}
                    >
                        <MdViewList />
                    </button>
                </div>
            </div>

            {/*
              * Barra de ações da seleção.
              *
              * Flutua no rodapé em vez de empurrar a lista: quem seleciona no
              * fim de uma pasta longa não deve ter que rolar de volta ao topo
              * para achar o botão. Aparece com o modo ligado mesmo sem nada
              * marcado — é ela que diz o que fazer em seguida.
              */}
            {selectionMode && (
                <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[60] flex justify-center px-4">
                    <div className="pointer-events-auto flex flex-wrap items-center justify-center gap-3 rounded-2xl border border-base-300 bg-base-100 px-4 py-2.5 shadow-2xl">
                        <span className="text-sm font-medium text-base-content">
                            {selectionCount
                                ? `${selectionCount} ${
                                      selectionCount === 1
                                          ? 'item selecionado'
                                          : 'itens selecionados'
                                  }`
                                : 'Marque os itens que deseja mover'}
                        </span>
                        <button
                            type="button"
                            className="btn btn-sm btn-ghost"
                            onClick={toggleAll}
                            disabled={!files.length && !folders.length}
                        >
                            {allSelected ? 'Desmarcar tudo' : 'Selecionar tudo'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary gap-2"
                            disabled={!selectionCount}
                            onClick={() =>
                                setMovePayload({
                                    fileIds: selectedFileIds,
                                    folderIds: selectedFolderIds,
                                })
                            }
                        >
                            <MdDriveFileMove className="text-base" />
                            Mover para
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-ghost"
                            onClick={exitSelectionMode}
                        >
                            Concluir
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg text-primary" />
                </div>
            ) : isEmpty ? (
                <div className="rounded-2xl border border-base-300 bg-base-100 p-12 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-base-200 text-3xl text-base-content/30">
                        {trash ? <MdDelete /> : <MdFolder />}
                    </div>
                    <h3 className="mb-2 text-lg font-semibold text-base-content">
                        {trash
                            ? 'A lixeira está vazia'
                            : debouncedSearch
                              ? 'Nenhum arquivo encontrado'
                              : 'Esta pasta está vazia'}
                    </h3>
                    <p className="mb-6 text-sm text-base-content/50">
                        {trash
                            ? 'Itens excluídos aparecem aqui antes da remoção definitiva.'
                            : debouncedSearch
                              ? 'Tente outro termo ou verifique a pasta.'
                              : 'Envie arquivos ou crie uma pasta para começar.'}
                    </p>
                    {!trash && !debouncedSearch && canCreateFiles && (
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm gap-2"
                            onClick={() => setUploadOpen(true)}
                        >
                            <MdCloudUpload className="text-base" />
                            Enviar arquivos
                        </button>
                    )}
                </div>
            ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                    {folders.map(folder => (
                        <div
                            key={`folder-${folder.id}`}
                            {...folderDropProps(folder.id)}
                            className={`group relative flex flex-col rounded-2xl border p-4 transition-colors ${
                                dropFolderId === folder.id
                                    ? 'border-primary border-2 bg-primary/10 ring-2 ring-primary/30'
                                    : selectedFolderIds.includes(folder.id)
                                      ? 'border-primary bg-primary/5'
                                      : 'border-base-300 bg-base-100 hover:border-secondary/40 hover:bg-base-200/30'
                            }`}
                        >
                            {selectionMode && (
                                <label className="absolute left-2 top-2 z-10 cursor-pointer p-1">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-sm checkbox-primary"
                                        checked={selectedFolderIds.includes(folder.id)}
                                        onChange={() => toggleFolder(folder.id)}
                                        aria-label={`Selecionar a pasta ${folder.name}`}
                                    />
                                </label>
                            )}

                            {/* Menu sempre presente, não só no hover: em telas de
                                toque não existe hover, e as ações ficavam
                                inalcançáveis. */}
                            <div className="absolute right-2 top-2 z-10">
                                <div className="dropdown dropdown-end">
                                    <div
                                        tabIndex={0}
                                        role="button"
                                        className="btn btn-ghost btn-xs btn-circle"
                                        aria-label="Ações da pasta"
                                    >
                                        <MdMoreVert className="text-base" />
                                    </div>
                                    <ul
                                        tabIndex={0}
                                        onClick={closeDropdown}
                                        className="dropdown-content menu z-20 w-52 rounded-xl border border-base-300 bg-base-100 p-1.5 shadow-lg"
                                    >
                                        {trash ? (
                                            <li>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleRestore(
                                                            'folder',
                                                            folder.id,
                                                        )
                                                    }
                                                >
                                                    <MdRestore />
                                                    Restaurar
                                                </button>
                                            </li>
                                        ) : (
                                            <>
                                                <li>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            void openFolder(
                                                                folder,
                                                            )
                                                        }
                                                    >
                                                        <MdVisibility />
                                                        Abrir
                                                    </button>
                                                </li>
                                                {canUpdateFiles && (
                                                    <>
                                                        <li>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setRenaming(
                                                                        {
                                                                            kind: 'folder',
                                                                            id: folder.id,
                                                                        },
                                                                    )
                                                                    setRenameValue(
                                                                        folder.name,
                                                                    )
                                                                }}
                                                            >
                                                                <MdEdit />
                                                                Renomear
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    setMovePayload({
                                                                        fileIds: [],
                                                                        folderIds: [folder.id],
                                                                    })
                                                                }
                                                            >
                                                                <MdDriveFileMove />
                                                                Mover para
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    void openPasswordSettings(
                                                                        {
                                                                            kind: 'folder',
                                                                            id: folder.id,
                                                                            name: folder.name,
                                                                            hint: folder.passwordHint,
                                                                            hasPassword:
                                                                                folder.hasPassword,
                                                                        },
                                                                    )
                                                                }
                                                            >
                                                                {folder.hasPassword ? (
                                                                    <MdLock />
                                                                ) : (
                                                                    <MdLockOpen />
                                                                )}
                                                                {folder.hasPassword
                                                                    ? 'Alterar senha'
                                                                    : 'Proteger com senha'}
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    setAccessFolder(
                                                                        folder,
                                                                    )
                                                                }
                                                            >
                                                                <MdShield />
                                                                Perfis com
                                                                acesso
                                                            </button>
                                                        </li>
                                                    </>
                                                )}
                                                {canDeleteFiles && (
                                                    <li>
                                                        <button
                                                            type="button"
                                                            className="text-error"
                                                            onClick={() =>
                                                                setConfirmDelete(
                                                                    {
                                                                        kind: 'folder',
                                                                        id: folder.id,
                                                                        name: folder.name,
                                                                    },
                                                                )
                                                            }
                                                        >
                                                            <MdDelete />
                                                            Excluir
                                                        </button>
                                                    </li>
                                                )}
                                            </>
                                        )}
                                    </ul>
                                </div>
                            </div>

                            {/* Clique simples abre. O duplo clique de antes não
                                se anuncia em lugar nenhum e não existe em toque. */}
                            <button
                                type="button"
                                className="flex min-w-0 flex-1 flex-col items-center gap-2 text-center"
                                onClick={() =>
                                    selectionMode
                                        ? toggleFolder(folder.id)
                                        : !trash && void openFolder(folder)
                                }
                                disabled={trash && !selectionMode}
                            >
                                <span className="relative">
                                    <MdFolder
                                        className={`text-5xl ${folderColorClass(folder)}`}
                                        style={
                                            folder.color
                                                ? { color: folder.color }
                                                : undefined
                                        }
                                    />
                                    {folder.restricted && (
                                        <MdShield
                                            className="absolute -right-1 -top-1 rounded-full bg-base-100 p-0.5 text-base text-secondary"
                                            title="Pasta restrita"
                                        />
                                    )}
                                    {folder.hasPassword && (
                                        <MdLock
                                            className="absolute -bottom-1 -right-1 rounded-full bg-base-100 p-0.5 text-base text-warning"
                                            title="Protegida por senha"
                                        />
                                    )}
                                </span>

                                {renaming?.kind === 'folder' &&
                                renaming.id === folder.id ? (
                                    <input
                                        type="text"
                                        className="input input-bordered input-xs w-full"
                                        value={renameValue}
                                        autoFocus
                                        onClick={event =>
                                            event.stopPropagation()
                                        }
                                        onChange={event =>
                                            setRenameValue(event.target.value)
                                        }
                                        onBlur={handleRename}
                                        onKeyDown={event => {
                                            if (event.key === 'Enter')
                                                void handleRename()
                                            if (event.key === 'Escape')
                                                setRenaming(null)
                                        }}
                                    />
                                ) : (
                                    <span
                                        className="w-full truncate text-sm font-medium text-base-content"
                                        title={folder.name}
                                    >
                                        {folder.name}
                                    </span>
                                )}

                                <span className="w-full truncate text-xs text-base-content/45">
                                    {folder.restricted
                                        ? `Restrita a ${folder.roleIds?.length ?? 0} perfil(is)`
                                        : `${folder.fileCount} item(ns)`}
                                </span>
                            </button>
                        </div>
                    ))}

                    {files.map(file => {
                        const Icon = iconOf(file.mimeType, file.extension)
                        return (
                            <div
                                key={`file-${file.id}`}
                                className={`group relative flex flex-col rounded-2xl border p-4 transition-colors ${
                                    selectedFileIds.includes(file.id)
                                        ? 'border-primary bg-primary/5'
                                        : 'border-base-300 bg-base-100 hover:border-secondary/40 hover:bg-base-200/30'
                                }`}
                            >
                                {selectionMode && (
                                    <label className="absolute left-2 top-2 z-10 cursor-pointer p-1">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-sm checkbox-primary"
                                            checked={selectedFileIds.includes(file.id)}
                                            onChange={() => toggleFile(file.id)}
                                            aria-label={`Selecionar o arquivo ${file.name}`}
                                        />
                                    </label>
                                )}

                                <div className="absolute right-2 top-2 z-10">
                                    <div className="dropdown dropdown-end">
                                        <div
                                            tabIndex={0}
                                            role="button"
                                            className="btn btn-ghost btn-xs btn-circle"
                                            aria-label="Ações do arquivo"
                                        >
                                            <MdMoreVert className="text-base" />
                                        </div>
                                        <ul
                                            tabIndex={0}
                                            onClick={closeDropdown}
                                            className="dropdown-content menu z-20 w-52 rounded-xl border border-base-300 bg-base-100 p-1.5 shadow-lg"
                                        >
                                            {trash ? (
                                                <>
                                                    <li>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                handleRestore(
                                                                    'file',
                                                                    file.id,
                                                                )
                                                            }
                                                        >
                                                            <MdRestore />
                                                            Restaurar
                                                        </button>
                                                    </li>
                                                    {canDeleteFiles && (
                                                        <li>
                                                            <button
                                                                type="button"
                                                                className="text-error"
                                                                onClick={() =>
                                                                    setConfirmDelete(
                                                                        {
                                                                            kind: 'file',
                                                                            id: file.id,
                                                                            name: file.name,
                                                                            permanent: true,
                                                                        },
                                                                    )
                                                                }
                                                            >
                                                                <MdDelete />
                                                                Excluir
                                                                definitivamente
                                                            </button>
                                                        </li>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <li>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                void handleDownload(
                                                                    file,
                                                                    true,
                                                                )
                                                            }
                                                        >
                                                            <MdVisibility />
                                                            Abrir
                                                        </button>
                                                    </li>
                                                    <li>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                void handleDownload(
                                                                    file,
                                                                )
                                                            }
                                                        >
                                                            <MdDownload />
                                                            Baixar
                                                        </button>
                                                    </li>
                                                    <li>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setDetailsFile(
                                                                    file,
                                                                )
                                                            }
                                                        >
                                                            <MdInfoOutline />
                                                            Detalhes
                                                        </button>
                                                    </li>
                                                    <li>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setShareFile(
                                                                    file,
                                                                )
                                                            }
                                                        >
                                                            <MdLink />
                                                            Compartilhar
                                                        </button>
                                                    </li>
                                                    {canUpdateFiles && (
                                                        <>
                                                            <li>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setRenaming(
                                                                            {
                                                                                kind: 'file',
                                                                                id: file.id,
                                                                            },
                                                                        )
                                                                        setRenameValue(
                                                                            file.name,
                                                                        )
                                                                    }}
                                                                >
                                                                    <MdEdit />
                                                                    Renomear
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        setMovePayload({
                                                                            fileIds: [file.id],
                                                                            folderIds: [],
                                                                        })
                                                                    }
                                                                >
                                                                    <MdDriveFileMove />
                                                                    Mover para
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        void openPasswordSettings(
                                                                            {
                                                                                kind: 'file',
                                                                                id: file.id,
                                                                                name: file.name,
                                                                                hint: file.passwordHint,
                                                                                hasPassword:
                                                                                    file.hasPassword,
                                                                            },
                                                                        )
                                                                    }
                                                                >
                                                                    {file.hasPassword ? (
                                                                        <MdLock />
                                                                    ) : (
                                                                        <MdLockOpen />
                                                                    )}
                                                                    {file.hasPassword
                                                                        ? 'Alterar senha'
                                                                        : 'Proteger com senha'}
                                                                </button>
                                                            </li>
                                                        </>
                                                    )}
                                                    {canDeleteFiles && (
                                                        <li>
                                                            <button
                                                                type="button"
                                                                className="text-error"
                                                                onClick={() =>
                                                                    setConfirmDelete(
                                                                        {
                                                                            kind: 'file',
                                                                            id: file.id,
                                                                            name: file.name,
                                                                        },
                                                                    )
                                                                }
                                                            >
                                                                <MdDelete />
                                                                Excluir
                                                            </button>
                                                        </li>
                                                    )}
                                                </>
                                            )}
                                        </ul>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="flex min-w-0 flex-1 flex-col items-center gap-2 text-center"
                                    onClick={() =>
                                        selectionMode
                                            ? toggleFile(file.id)
                                            : !trash && void handleDownload(file, true)
                                    }
                                    disabled={
                                        selectionMode
                                            ? false
                                            : trash || openingFileId === file.id
                                    }
                                >
                                    <span className="relative">
                                        {/* O ícone vira indicador enquanto a URL
                                            é assinada — o clique precisa dar
                                            sinal de vida no próprio item. */}
                                        {openingFileId === file.id ? (
                                            <span className="loading loading-spinner loading-lg text-secondary" />
                                        ) : (
                                            <Icon
                                                className={`text-5xl ${colorOf(
                                                    file.mimeType,
                                                    file.extension,
                                                )}`}
                                            />
                                        )}
                                        {file.hasPassword && (
                                            <MdLock
                                                className="absolute -bottom-1 -right-1 rounded-full bg-base-100 p-0.5 text-base text-warning"
                                                title="Protegido por senha"
                                            />
                                        )}
                                    </span>

                                    {renaming?.kind === 'file' &&
                                    renaming.id === file.id ? (
                                        <input
                                            type="text"
                                            className="input input-bordered input-xs w-full"
                                            value={renameValue}
                                            autoFocus
                                            onClick={event =>
                                                event.stopPropagation()
                                            }
                                            onChange={event =>
                                                setRenameValue(
                                                    event.target.value,
                                                )
                                            }
                                            onBlur={handleRename}
                                            onKeyDown={event => {
                                                if (event.key === 'Enter')
                                                    void handleRename()
                                                if (event.key === 'Escape')
                                                    setRenaming(null)
                                            }}
                                        />
                                    ) : (
                                        <span
                                            className="w-full truncate text-sm font-medium text-base-content"
                                            title={file.name}
                                        >
                                            {file.name}
                                        </span>
                                    )}

                                    <span className="w-full truncate text-xs text-base-content/45">
                                        {formatBytes(file.sizeBytes)}
                                    </span>
                                </button>
                            </div>
                        )
                    })}
                </div>

            ) : (
                /* Sem `overflow` no caminho: o menu de ações é `absolute`, e
                   qualquer container com overflow o cortava nas últimas linhas.
                   Com `table-fixed` e truncate no nome a tabela cabe sem
                   rolagem horizontal, então não há o que rolar. */
                <div className="rounded-2xl border border-base-300 bg-base-100">
                    <table className="table table-fixed table-sm w-full">
                            <thead>
                                <tr>
                                    {selectionMode && (
                                        <th className="w-10">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-sm checkbox-primary"
                                                checked={allSelected}
                                                onChange={toggleAll}
                                                aria-label="Selecionar todos os itens"
                                            />
                                        </th>
                                    )}
                                    <th>Nome</th>
                                    <th className="w-28">Tamanho</th>
                                    <th className="hidden w-44 md:table-cell">
                                        Modificado
                                    </th>
                                    <th className="w-14 text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {folders.map(folder => (
                                    <tr
                                        key={`folder-${folder.id}`}
                                        {...folderDropProps(folder.id)}
                                        className={
                                            dropFolderId === folder.id
                                                ? 'bg-primary/10 outline outline-2 outline-primary/40'
                                                : selectedFolderIds.includes(folder.id)
                                                  ? 'bg-primary/5'
                                                  : 'hover'
                                        }
                                    >
                                        {selectionMode && (
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm checkbox-primary"
                                                    checked={selectedFolderIds.includes(
                                                        folder.id,
                                                    )}
                                                    onChange={() => toggleFolder(folder.id)}
                                                    aria-label={`Selecionar a pasta ${folder.name}`}
                                                />
                                            </td>
                                        )}
                                        <td className="max-w-0">
                                            <button
                                                type="button"
                                                className="flex w-full min-w-0 items-center gap-2.5 text-left transition-colors hover:text-secondary disabled:hover:text-base-content"
                                                onClick={() =>
                                                    selectionMode
                                                        ? toggleFolder(folder.id)
                                                        : !trash && void openFolder(folder)
                                                }
                                                disabled={trash && !selectionMode}
                                            >
                                                <MdFolder
                                                    className={`shrink-0 text-xl ${folderColorClass(folder)}`}
                                                    style={
                                                        folder.color
                                                            ? { color: folder.color }
                                                            : undefined
                                                    }
                                                />
                                                <span
                                                    className="truncate font-medium"
                                                    title={folder.name}
                                                >
                                                    {folder.name}
                                                </span>
                                                {folder.restricted && (
                                                    <span
                                                        className="badge badge-sm badge-ghost shrink-0 gap-1"
                                                        title="Pasta restrita a perfis de acesso"
                                                    >
                                                        <MdShield className="text-secondary" />
                                                        Restrita
                                                    </span>
                                                )}
                                                {folder.hasPassword && (
                                                    <MdLock
                                                        className="shrink-0 text-warning"
                                                        title="Protegida por senha"
                                                    />
                                                )}
                                            </button>
                                        </td>
                                        <td className="whitespace-nowrap text-base-content/50">
                                            {formatBytes(folder.sizeBytes)}
                                        </td>
                                        <td className="hidden whitespace-nowrap text-base-content/50 md:table-cell">
                                            {formatDateTime(folder.updatedAt)}
                                        </td>
                                        <td className="text-right">
                                            {trash ? (
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-xs btn-square"
                                                    onClick={() =>
                                                        handleRestore('folder', folder.id)
                                                    }
                                                    title="Restaurar"
                                                >
                                                    <MdRestore />
                                                </button>
                                            ) : (
                                                <div className="dropdown dropdown-end">
                                                    <div
                                                        tabIndex={0}
                                                        role="button"
                                                        className="btn btn-ghost btn-xs btn-circle"
                                                        aria-label="Ações da pasta"
                                                    >
                                                        <MdMoreVert className="text-base" />
                                                    </div>
                                                    <ul
                                                        tabIndex={0}
                                                        onClick={closeDropdown}
                                                        className="dropdown-content menu z-20 w-52 rounded-xl border border-base-300 bg-base-100 p-1.5 text-left shadow-lg"
                                                    >
                                                        <li>
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    void openFolder(folder)
                                                                }
                                                            >
                                                                <MdVisibility />
                                                                Abrir
                                                            </button>
                                                        </li>
                                                        {canUpdateFiles && (
                                                            <>
                                                                <li>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setRenaming({
                                                                                kind: 'folder',
                                                                                id: folder.id,
                                                                            })
                                                                            setRenameValue(
                                                                                folder.name,
                                                                            )
                                                                        }}
                                                                    >
                                                                        <MdEdit />
                                                                        Renomear
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            setMovePayload({
                                                                                fileIds: [],
                                                                                folderIds: [folder.id],
                                                                            })
                                                                        }
                                                                    >
                                                                        <MdDriveFileMove />
                                                                        Mover para
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            void openPasswordSettings({
                                                                                kind: 'folder',
                                                                                id: folder.id,
                                                                                name: folder.name,
                                                                                hint: folder.passwordHint,
                                                                                hasPassword:
                                                                                    folder.hasPassword,
                                                                            })
                                                                        }
                                                                    >
                                                                        {folder.hasPassword ? (
                                                                            <MdLock />
                                                                        ) : (
                                                                            <MdLockOpen />
                                                                        )}
                                                                        {folder.hasPassword
                                                                            ? 'Alterar senha'
                                                                            : 'Proteger com senha'}
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            setAccessFolder(
                                                                                folder,
                                                                            )
                                                                        }
                                                                    >
                                                                        <MdShield />
                                                                        Perfis com acesso
                                                                    </button>
                                                                </li>
                                                            </>
                                                        )}
                                                        {canDeleteFiles && (
                                                            <li>
                                                                <button
                                                                    type="button"
                                                                    className="text-error"
                                                                    onClick={() =>
                                                                        setConfirmDelete({
                                                                            kind: 'folder',
                                                                            id: folder.id,
                                                                            name: folder.name,
                                                                        })
                                                                    }
                                                                >
                                                                    <MdDelete />
                                                                    Excluir
                                                                </button>
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}

                                {files.map(file => {
                                    const Icon = iconOf(file.mimeType, file.extension)
                                    return (
                                        <tr
                                            key={`file-${file.id}`}
                                            className={
                                                selectedFileIds.includes(file.id)
                                                    ? 'bg-primary/5'
                                                    : 'hover'
                                            }
                                        >
                                            {selectionMode && (
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        className="checkbox checkbox-sm checkbox-primary"
                                                        checked={selectedFileIds.includes(
                                                            file.id,
                                                        )}
                                                        onChange={() => toggleFile(file.id)}
                                                        aria-label={`Selecionar o arquivo ${file.name}`}
                                                    />
                                                </td>
                                            )}
                                            <td className="max-w-0">
                                                <button
                                                    type="button"
                                                    className="flex w-full min-w-0 items-center gap-2.5 text-left transition-colors hover:text-secondary disabled:hover:text-base-content"
                                                    onClick={() =>
                                                        selectionMode
                                                            ? toggleFile(file.id)
                                                            : !trash &&
                                                              void handleDownload(file, true)
                                                    }
                                                    disabled={
                                                        selectionMode
                                                            ? false
                                                            : trash ||
                                                              openingFileId === file.id
                                                    }
                                                >
                                                    {openingFileId === file.id ? (
                                                        <span className="loading loading-spinner loading-xs shrink-0 text-secondary" />
                                                    ) : (
                                                        <Icon
                                                            className={`shrink-0 text-xl ${colorOf(
                                                                file.mimeType,
                                                                file.extension,
                                                            )}`}
                                                        />
                                                    )}
                                                    <span
                                                        className="truncate"
                                                        title={file.name}
                                                    >
                                                        {file.name}
                                                    </span>
                                                    {file.hasPassword && (
                                                        <MdLock
                                                            className="shrink-0 text-warning"
                                                            title="Protegido por senha"
                                                        />
                                                    )}
                                                </button>
                                            </td>
                                            <td className="whitespace-nowrap text-base-content/50">
                                                {formatBytes(file.sizeBytes)}
                                            </td>
                                            <td className="hidden whitespace-nowrap text-base-content/50 md:table-cell">
                                                {formatDateTime(file.updatedAt)}
                                            </td>
                                            <td className="text-right">
                                                <div className="dropdown dropdown-end">
                                                    <div
                                                        tabIndex={0}
                                                        role="button"
                                                        className="btn btn-ghost btn-xs btn-circle"
                                                        aria-label="Ações do arquivo"
                                                    >
                                                        <MdMoreVert className="text-base" />
                                                    </div>
                                                    <ul
                                                        tabIndex={0}
                                                        onClick={closeDropdown}
                                                        className="dropdown-content menu z-20 w-56 rounded-xl border border-base-300 bg-base-100 p-1.5 text-left shadow-lg"
                                                    >
                                                        {trash ? (
                                                            <>
                                                                <li>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            handleRestore(
                                                                                'file',
                                                                                file.id,
                                                                            )
                                                                        }
                                                                    >
                                                                        <MdRestore />
                                                                        Restaurar
                                                                    </button>
                                                                </li>
                                                                {canDeleteFiles && (
                                                                    <li>
                                                                        <button
                                                                            type="button"
                                                                            className="text-error"
                                                                            onClick={() =>
                                                                                setConfirmDelete({
                                                                                    kind: 'file',
                                                                                    id: file.id,
                                                                                    name: file.name,
                                                                                    permanent: true,
                                                                                })
                                                                            }
                                                                        >
                                                                            <MdDelete />
                                                                            Excluir
                                                                            definitivamente
                                                                        </button>
                                                                    </li>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <li>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            void handleDownload(
                                                                                file,
                                                                                true,
                                                                            )
                                                                        }
                                                                    >
                                                                        <MdVisibility />
                                                                        Abrir
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            void handleDownload(
                                                                                file,
                                                                            )
                                                                        }
                                                                    >
                                                                        <MdDownload />
                                                                        Baixar
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            setDetailsFile(file)
                                                                        }
                                                                    >
                                                                        <MdInfoOutline />
                                                                        Detalhes
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            setShareFile(file)
                                                                        }
                                                                    >
                                                                        <MdLink />
                                                                        Compartilhar
                                                                    </button>
                                                                </li>
                                                                {canUpdateFiles && (
                                                                    <>
                                                                        <li>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setRenaming({
                                                                                        kind: 'file',
                                                                                        id: file.id,
                                                                                    })
                                                                                    setRenameValue(
                                                                                        file.name,
                                                                                    )
                                                                                }}
                                                                            >
                                                                                <MdEdit />
                                                                                Renomear
                                                                            </button>
                                                                        </li>
                                                                        <li>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() =>
                                                                                    setMovePayload({
                                                                                        fileIds: [file.id],
                                                                                        folderIds: [],
                                                                                    })
                                                                                }
                                                                            >
                                                                                <MdDriveFileMove />
                                                                                Mover para
                                                                            </button>
                                                                        </li>
                                                                        <li>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() =>
                                                                                    void openPasswordSettings(
                                                                                        {
                                                                                            kind: 'file',
                                                                                            id: file.id,
                                                                                            name: file.name,
                                                                                            hint: file.passwordHint,
                                                                                            hasPassword:
                                                                                                file.hasPassword,
                                                                                        },
                                                                                    )
                                                                                }
                                                                            >
                                                                                {file.hasPassword ? (
                                                                                    <MdLock />
                                                                                ) : (
                                                                                    <MdLockOpen />
                                                                                )}
                                                                                {file.hasPassword
                                                                                    ? 'Alterar senha'
                                                                                    : 'Proteger com senha'}
                                                                            </button>
                                                                        </li>
                                                                    </>
                                                                )}
                                                                {canDeleteFiles && (
                                                                    <li>
                                                                        <button
                                                                            type="button"
                                                                            className="text-error"
                                                                            onClick={() =>
                                                                                setConfirmDelete({
                                                                                    kind: 'file',
                                                                                    id: file.id,
                                                                                    name: file.name,
                                                                                })
                                                                            }
                                                                        >
                                                                            <MdDelete />
                                                                            Excluir
                                                                        </button>
                                                                    </li>
                                                                )}
                                                            </>
                                                        )}
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                    </table>
                </div>
            )}

            {/* Nova pasta */}
            {newFolderOpen && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-md">
                        <h3 className="text-lg font-semibold mb-5">Nova pasta</h3>

                        <div className="space-y-4">
                            <div className="form-control w-full">
                                <label className="label pb-1.5" htmlFor="new-folder-name">
                                    <span className="label-text font-medium">Nome da pasta</span>
                                </label>
                                <input
                                    id="new-folder-name"
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={newFolderName}
                                    autoFocus
                                    onChange={event => setNewFolderName(event.target.value)}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter' && !newFolderRestricted) {
                                            void handleCreateFolder()
                                        }
                                    }}
                                />
                            </div>

                            {/* Tipo de pasta. Restrita só aparece para quem tem
                                permissão de edição — é ela que decide quem enxerga
                                o quê, e o backend recusa se faltar. */}
                            {canUpdateFiles && (
                                <div className="form-control w-full">
                                    <label className="label pb-1.5">
                                        <span className="label-text font-medium">
                                            Tipo de pasta
                                        </span>
                                    </label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            className={`btn btn-sm h-auto py-2.5 flex-col gap-1 ${
                                                newFolderRestricted
                                                    ? 'btn-ghost border border-base-300'
                                                    : 'btn-primary'
                                            }`}
                                            onClick={() => setNewFolderRestricted(false)}
                                        >
                                            <MdFolder className="text-lg" />
                                            <span className="text-xs font-normal">Padrão</span>
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-sm h-auto py-2.5 flex-col gap-1 ${
                                                newFolderRestricted
                                                    ? 'btn-primary'
                                                    : 'btn-ghost border border-base-300'
                                            }`}
                                            onClick={() => setNewFolderRestricted(true)}
                                        >
                                            <MdShield className="text-lg" />
                                            <span className="text-xs font-normal">Restrita</span>
                                        </button>
                                    </div>
                                    <span className="label-text-alt text-base-content/50 mt-1.5">
                                        {newFolderRestricted
                                            ? 'Visível apenas aos perfis de acesso selecionados'
                                            : 'Visível a todos que acessam o Armazenamento'}
                                    </span>
                                </div>
                            )}

                            {newFolderRestricted && (
                                <div className="form-control w-full">
                                    <label className="label pb-1.5">
                                        <span className="label-text font-medium">
                                            Perfis com acesso
                                        </span>
                                    </label>
                                    <StorageRolePicker
                                        selected={newFolderRoleIds}
                                        onChange={setNewFolderRoleIds}
                                        disabled={savingFolder}
                                    />
                                </div>
                            )}

                            {newFolderPasswordEnabled ? (
                                <div className="form-control w-full">
                                    <label
                                        className="label pb-1.5"
                                        htmlFor="new-folder-password"
                                    >
                                        <span className="label-text font-medium">
                                            Senha da pasta
                                        </span>
                                        <button
                                            type="button"
                                            className="label-text-alt link link-hover text-base-content/60"
                                            onClick={() => {
                                                setNewFolderPasswordEnabled(false)
                                                setNewFolderPassword('')
                                            }}
                                        >
                                            Remover proteção
                                        </button>
                                    </label>
                                    <input
                                        id="new-folder-password"
                                        type="password"
                                        className="input input-bordered w-full"
                                        value={newFolderPassword}
                                        autoFocus
                                        placeholder="Digite a senha desta pasta"
                                        onChange={event =>
                                            setNewFolderPassword(event.target.value)
                                        }
                                    />
                                    <span className="label-text-alt text-base-content/50 mt-1.5">
                                        Será pedida a quem abrir a pasta
                                    </span>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-ghost gap-2 self-start text-base-content/70"
                                    onClick={() => setNewFolderPasswordEnabled(true)}
                                >
                                    <MdLock className="text-base" />
                                    Proteger com senha
                                </button>
                            )}
                        </div>

                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={closeNewFolder}
                                disabled={savingFolder}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleCreateFolder}
                                disabled={savingFolder}
                            >
                                {savingFolder && (
                                    <span className="loading loading-spinner loading-sm" />
                                )}
                                Criar pasta
                            </button>
                        </div>
                    </div>
                    <div className="modal-backdrop" onClick={closeNewFolder} />
                </div>
            )}

            {/* Detalhes do arquivo */}
            {detailsFile && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-lg">
                        <h3 className="text-lg font-semibold mb-5">Detalhes do arquivo</h3>

                        <dl className="space-y-3 text-sm">
                            <div className="flex justify-between gap-4">
                                <dt className="text-base-content/60">Nome</dt>
                                <dd className="font-medium text-right break-all">
                                    {detailsFile.name}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-base-content/60">Tipo</dt>
                                <dd className="text-right">{detailsFile.mimeType}</dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-base-content/60">Tamanho</dt>
                                <dd className="text-right">
                                    {formatBytes(detailsFile.sizeBytes)}
                                </dd>
                            </div>
                            {detailsFile.width && detailsFile.height && (
                                <div className="flex justify-between gap-4">
                                    <dt className="text-base-content/60">Dimensões</dt>
                                    <dd className="text-right">
                                        {detailsFile.width} x {detailsFile.height} px
                                    </dd>
                                </div>
                            )}
                            <div className="flex justify-between gap-4">
                                <dt className="text-base-content/60">Versão</dt>
                                <dd className="text-right">{detailsFile.currentVersion}</dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-base-content/60">Downloads</dt>
                                <dd className="text-right">{detailsFile.downloadCount}</dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-base-content/60">Enviado em</dt>
                                <dd className="text-right">
                                    {formatDateTime(detailsFile.createdAt)}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-base-content/60">Último acesso</dt>
                                <dd className="text-right">
                                    {formatDateTime(detailsFile.lastAccessAt)}
                                </dd>
                            </div>
                            {detailsFile.description && (
                                <div>
                                    <dt className="text-base-content/60 mb-1">Descrição</dt>
                                    <dd>{detailsFile.description}</dd>
                                </div>
                            )}
                            {detailsFile.tags.length > 0 && (
                                <div>
                                    <dt className="text-base-content/60 mb-1">Etiquetas</dt>
                                    <dd className="flex flex-wrap gap-1">
                                        {detailsFile.tags.map(tag => (
                                            <span key={tag} className="badge badge-sm">
                                                {tag}
                                            </span>
                                        ))}
                                    </dd>
                                </div>
                            )}
                        </dl>

                        <div className="modal-action">
                            {canUpdateFiles && (
                                <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={() => {
                                        void openPasswordSettings({
                                            kind: 'file',
                                            id: detailsFile.id,
                                            name: detailsFile.name,
                                            hint: detailsFile.passwordHint,
                                            hasPassword: detailsFile.hasPassword,
                                        })
                                        setDetailsFile(null)
                                    }}
                                >
                                    {detailsFile.hasPassword ? <MdLock /> : <MdLockOpen />}
                                    Senha
                                </button>
                            )}
                            {canCreateFiles && (
                                <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={() => {
                                        void handleCopy(detailsFile)
                                        setDetailsFile(null)
                                    }}
                                >
                                    <MdContentCopy />
                                    Duplicar
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => setDetailsFile(null)}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                    <div className="modal-backdrop" onClick={() => setDetailsFile(null)} />
                </div>
            )}

            <StorageUploadModal
                key={uploadSession}
                isOpen={uploadOpen}
                folderId={droppedFiles?.length ? uploadTargetFolderId : folderId}
                initialFiles={droppedFiles ?? undefined}
                autoStart={Boolean(droppedFiles?.length)}
                onClose={() => {
                    setUploadOpen(false)
                    setDroppedFiles(null)
                    setUploadTargetFolderId(null)
                }}
                onCompleted={() => {
                    void load()
                    void loadUsage()
                }}
            />

            {passwordModal && (
                <StoragePasswordModal
                    isOpen
                    mode={passwordModal.mode}
                    targetName={passwordModal.target.name}
                    hint={passwordModal.target.hint}
                    hasPassword={passwordModal.target.hasPassword}
                    loading={passwordLoading}
                    onClose={() => {
                        setPasswordModal(null)
                        setPasswordSettingsAfterUnlock(null)
                    }}
                    onConfirm={handlePasswordConfirm}
                />
            )}

            <StorageShareModal
                isOpen={Boolean(shareFile)}
                file={shareFile}
                onClose={() => setShareFile(null)}
            />

            <StorageMoveModal
                isOpen={Boolean(movePayload)}
                fileCount={movePayload?.fileIds.length ?? 0}
                movingFolderIds={movePayload?.folderIds ?? []}
                /* Na busca os itens podem vir de pastas diferentes, então não há
                   uma "pasta atual" para descartar como destino. */
                currentFolderId={debouncedSearch ? undefined : folderId}
                moving={moving}
                onClose={() => setMovePayload(null)}
                onConfirm={handleMove}
            />

            <StorageAccessModal
                isOpen={Boolean(accessFolder)}
                folder={accessFolder}
                onClose={() => setAccessFolder(null)}
                onSaved={() => void load()}
            />

            <FilePreviewModal
                file={previewFile}
                onClose={() => setPreviewFile(null)}
            />

            <ConfirmModal
                isOpen={Boolean(confirmDelete)}
                onClose={() => setConfirmDelete(null)}
                onConfirm={handleDelete}
                loading={deleting}
                title={
                    confirmDelete?.permanent ? 'Excluir definitivamente' : 'Mover para a lixeira'
                }
                message={
                    confirmDelete?.permanent
                        ? `"${confirmDelete?.name}" será excluído definitivamente e não poderá ser recuperado. Deseja continuar?`
                        : `"${confirmDelete?.name}" será movido para a lixeira. Você poderá restaurá-lo depois.`
                }
                confirmButtonText={confirmDelete?.permanent ? 'Excluir' : 'Mover para a lixeira'}
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
